/**
 * Firebase User Service - TypeScript Migration
 */

import firebase from 'firebase/app';
import { firestore, storage, requestFirebaseNotificationPermission } from './config';
import { updatePassword } from './auth';
import {
  User,
  UserUpdateData,
  UserReadingInput,
  DeviceInformation,
} from '@/types/user.types';
import {
  DeviceType,
  Reading,
  NavigatorInfo,
} from '@/types/reading.types';

/**
 * Creates a user document in Firestore after signup
 */
export const createUserDocument = async (user: firebase.User): Promise<void> => {
  const docRef = firestore.doc(`/users/${user.uid}`);
  
  // Parse extra info from photoURL (temporary storage)
  const extraInfo = user.photoURL?.split('|') || ['false', ''];
  
  const userProfile: Partial<User> = {
    uid: user.uid,
    email: user.email || '',
    name: user.displayName || '',
    receiveEmail: extraInfo[0] === 'true',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: extraInfo[1] || '',
    gender: '',
    dob: '',
    weight: '',
    height: '',
    devices: ['bloodPressureMonitor'] as DeviceType[],
    doctor: '',
    created: new Date(user.metadata.creationTime || Date.now()),
  };

  await docRef.set(userProfile);
};

/**
 * Updates user document in Firestore
 */
export const updateUserDocument = async (user: UserUpdateData): Promise<any> => {
  console.log('User update:', user);

  if (!user.uid) {
    throw new Error('User UID is required for update');
  }

  const docRef = firestore.doc(`/users/${user.uid}`);
  
  // Separate password from other user data
  const { password, ...userPasswLess } = user;
  
  // Update password if provided
  if (password) {
    await updatePassword(password);
  }

  // Request Firebase notification permission and update token
  try {
    const firebaseToken = await requestFirebaseNotificationPermission();
    await docRef.update({
      firebaseToken: {
        key: firebaseToken,
        created: Date.now(),
      },
    });
  } catch (err) {
    console.error('Firebase token update error:', err);
  }

  return docRef.update(userPasswLess);
};

/**
 * Updates user override settings
 */
export const overrideUserDocument = async (user: {
  uid: string;
  override: any;
}): Promise<any> => {
  console.log('User override:', user);

  const docRef = firestore.doc(`/users/${user.uid}`);
  return docRef.update({ override: user });
};

/**
 * Sets device last reading information
 */
export const setUserDeviceLastReading = (
  uid: string,
  deviceType: string,
  lastReading: any,
  diff: number,
  readings: string[]
): Promise<any> => {
  const device: Record<string, any> = {};
  device[deviceType] = {
    lastReading: lastReading,
    updated: Date.now(),
  };

  console.log('setUserDeviceLastReading:', uid, deviceType, lastReading, device, diff);

  const docRef = firestore.doc(`/users/${uid}`);
  const alertMessage = `SmartViewMD saved ${diff} ${deviceType} device reading(s). ${readings.join(', ')}`;
  
  alert(alertMessage);
  console.log(alertMessage, device);

  return docRef.update({ deviceInfo: device });
};

/**
 * Sets device password/pairing information
 */
export const setUserDevicePW = (
  uid: string,
  deviceType: string,
  passWord: string,
  deviceInformation: DeviceInformation
): Promise<any> => {
  const device: Record<string, DeviceInformation> = {};
  device[deviceType] = {
    pw: passWord,
    paired: Date.now(),
    ...deviceInformation,
  };

  console.log('setUserDevicePW:', uid, deviceType, passWord, deviceInformation, device);

  const docRef = firestore.doc(`/users/${uid}`);
  const alertMessage = `SmartViewMD paired to ${deviceInformation?.manufacturer_name_string} ${deviceInformation?.model_number_string} ${deviceType} device.`;
  
  alert(alertMessage);
  console.log(alertMessage, device);

  return docRef.update({ deviceInfo: device });
};

/**
 * Gets device password from user document
 */
export const getUserDevicePW = async (
  uid: string,
  deviceType: string
): Promise<string | any[]> => {
  const docRef = firestore.doc(`/users/${uid}`);
  const doc = await docRef.get();
  
  const deviceInfo = doc.data()?.deviceInfo;
  const deviceData = deviceInfo?.[deviceType];
  
  // Return empty array if password doesn't exist, otherwise return the password
  return deviceData?.pw || [];
};

/**
 * Trims reading to only include devices with values
 */
function trimUserReading(readings: { devices: Record<string, any> }): {
  devices: Record<string, any>;
} {
  const readingWithValue: Record<string, any> = {};

  for (const device in readings.devices) {
    const deviceReading: Record<string, any> = {};
    deviceReading[device] = readings.devices[device];

    if (getAllVals(deviceReading) !== '') {
      Object.assign(readingWithValue, deviceReading);
    }
  }

  return { devices: readingWithValue };
}

/**
 * Recursively gets all values from an object
 */
function getAllVals(obj: any): string {
  let val = '';

  for (const k in obj) {
    if (typeof obj[k] === 'object') {
      val += getAllVals(obj[k]);
    } else {
      val += `${obj[k]}`.trim();
    }
  }

  return val;
}

/**
 * Adds a reading to user's readings collection
 */
export const addUserReading = async (
  user: UserReadingInput
): Promise<firebase.firestore.DocumentReference | null> => {
  const docRef = firestore.doc(`/users/${user.uid}`);
  const doc = await docRef.get();

  // Safely access doctor field
  const rawDoctor = doc.data()?.doctor;
  const isDoctorValid = rawDoctor && typeof rawDoctor === 'object' && rawDoctor.id;

  // Request Firebase token
  try {
    const firebaseToken = await requestFirebaseNotificationPermission();
    await docRef.update({
      firebaseToken: {
        key: firebaseToken,
        created: Date.now(),
      },
    });
  } catch (err) {
    console.error('Firebase token error:', err);
  }

  // Capture navigator info
  const navs: NavigatorInfo = {
    userAgent: navigator?.userAgent,
    appName: navigator?.appName,
    appVersion: navigator?.appVersion,
    vendor: navigator?.vendor,
    platform: navigator?.platform,
  };

  const batch = firestore.batch();

  // If multiple readings provided
  if (user?.adds?.length) {
    user.adds.forEach((add) => {
      const readingRef = docRef.collection('readings').doc();
      
      const reading: Partial<Reading> = {
        ...add.reading,
        navigator: navs,
      };

      if (user?.createdBy) {
        reading.createdBy = user.createdBy;
      }

      console.log(`ADD:`, reading);
      batch.set(readingRef, reading);
    });

    await batch.commit();
    return null;
  } else {
    // Single reading path
    if (!user.add) {
      return null;
    }

    const readingWithValue = trimUserReading(user.add);

    if (!readingWithValue || Object.entries(readingWithValue).length === 0) {
      return null;
    }

    const basePayload: Partial<Reading> = {
      created: Date.now(),
      ...readingWithValue,
      navigator: navs,
    };

    if (user?.createdBy) {
      basePayload.createdBy = user.createdBy;
    }

    if (isDoctorValid) {
      basePayload.profile = { doctor: rawDoctor };
    }

    return docRef.collection('readings').add(basePayload);
  }
};

/**
 * Uploads profile image to Firebase Storage
 */
export const uploadImage = (
  userId: string,
  file: File,
  progress: (snapshot: firebase.storage.UploadTaskSnapshot) => void
): Promise<firebase.storage.Reference> => {
  return new Promise((resolve, reject) => {
    const filePath = `users/${userId}/profile-image`;
    const fileRef = storage.ref().child(filePath);
    const uploadTask = fileRef.put(file);

    uploadTask.on(
      'state_changed',
      (snapshot) => progress(snapshot),
      (error) => reject(error),
      () => {
        resolve(uploadTask.snapshot.ref);
      }
    );
  });
};

/**
 * Gets download URL for user's profile image
 */
export const getDownloadUrl = (userId: string): Promise<string> => {
  const filePath = `users/${userId}/profile-image`;
  return storage.ref().child(filePath).getDownloadURL();
};